import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { CartService } from '../../../cart/core/services/CartService';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { ICard } from '../../core/interfaces/ICard';
import { ICardType } from '../../core/interfaces/ICardType';
import { AddItemsToCart, RemoveItemsFromCart } from '../../../../core/types';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-card-amount-picker-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    ContentBoxComponent,
    ContentEditableInputComponent,
    KeyboardComponent,
    NumberToCurrencyPipe,
    CommonModule
  ],
  templateUrl: './card-amount-picker-modal.component.html',
  styleUrl: './card-amount-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardAmountPickerModalComponent extends BaseModalComponent<ICardType> {

  @ViewChild(ContentEditableInputComponent) contentEditableInputComponent: ContentEditableInputComponent;

  public amountValue: string;
  public card: ICard;
  public isWithdraw = false;

  private _isAmountValueSet = true;
  private readonly _maxLoadCardAmount = 9999.99;
  private readonly _cartService = inject(CartService);
  private readonly _cartStore = inject(CartStore);

  public override open(val?: ICardType, card?: ICard, loadAmountOffset?: number, isWithdraw = false): void {
    this.isWithdraw = isWithdraw;
    super.open(cloneDeep(val));
    this.amountValue = null;
    this._isAmountValueSet = true;
    this.card = cloneDeep(card);
    const currentBalance = this.card?.currentBalance || 0;

    if (this.isWithdraw) {
      this.data.minLoadAmount = 0.01;
      this.data.maxLoadAmount = currentBalance;
      this.updateAmountValue(this.min.toString());
    } else {
      let calculatedMaxLoadAmount = this.data.maxLoadAmount || this._maxLoadCardAmount;
      if (this.data.maximumAmount > 0) {
        calculatedMaxLoadAmount = Math.min(calculatedMaxLoadAmount, this.data.maximumAmount - currentBalance);
      }
      
      if (loadAmountOffset && loadAmountOffset > 0) {
        const baseMinLoadAmount = this.data.minLoadAmount || 0;
        this.data.minLoadAmount = Math.max(baseMinLoadAmount, loadAmountOffset);
        this.data.maxLoadAmount = Math.min(calculatedMaxLoadAmount + loadAmountOffset, this._maxLoadCardAmount);
        const defaultAmount = this.data.minLoadAmount * 100;

        this.updateAmountValue(defaultAmount.toString());
      } else {
        this.data.maxLoadAmount = Math.min(calculatedMaxLoadAmount, this._maxLoadCardAmount);
        this.updateAmountValue(this.min.toString());
      }
    }
  }

  public get isConfirmDisabled(): boolean {
    const value = Number(this.amountValue);
    if (this.isWithdraw) {
      return value > this.max || value < this.min;
    }
    return value > this.max || value < this.min || !this.isValidIncrement(value);
  }

  public handleKeyboardClear(): void {
    this.updateAmountValue('');
  }

  public handleKeyboardDeleteBack(): void {
    this.updateAmountValue(this.amountValue.substring(0, this.amountValue.length - 1));
  }

  public handleKeyboardKeyClick(val: string): void {
    if (this._isAmountValueSet) {
      this._isAmountValueSet = false;
      this.handleKeyboardClear();
    }

    this.updateAmountValue(this.amountValue ? `${this.amountValue }${val}` : val);
  }

  public removeItemFromCart(btn: HTMLButtonElement): void {
    if (this.card) {
      const fCartItem = this._cartStore.cart().items.find(x => x.itemId === this.card.cardTypeId && x.cardId === this.card.cardId);

      if (fCartItem) {
        const items: RemoveItemsFromCart = [
          {
            itemId: fCartItem.itemId,
            position: fCartItem.position
          }
        ];

        btn.classList.add('disabled');

        this._cartService.removeItemFromCart(items)
          .pipe(
            finalize(() => btn.classList.remove('disabled')),
          )
          .subscribe({
            next: () => this.close(),
            error: ({ error }: ApiErrorResponse) => {
              this.notificationService.pushError(
                'Fehler'.trans('notifications/error.Title'),
                error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
              );
            }
          });
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  public addCardToCart(btn: HTMLButtonElement): void {
    if (this.isWithdraw) {
      this.withdrawCardBalance(btn);
      return;
    }

    const items: AddItemsToCart = [
      {
        itemId: this.data.cardTypeId,
        itemType: CartItemTypes.LoadCard,
        quantity: 1,
        price: this.contentEditableInputComponent.value.fromCurrency()
      }
    ];

    if (this.card) {
      items.forEach(i => i.cardNumber = this.card.cardNr);
    }

    btn.classList.add('disabled');

    this._cartService.addItemToCart(items)
      .pipe(
        finalize(() => btn.classList.remove('disabled')),
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-card-item-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public withdrawCardBalance(btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService.withdrawCardBalance(this.card.cardNr, this.contentEditableInputComponent.value.fromCurrency())
      .pipe(
        finalize(() => btn.classList.remove('disabled')),
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`withdraw-card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private get max(): number {
    return this.data?.maxLoadAmount * 100;
  }

  private get min(): number {
    return this.data?.minLoadAmount * 100;
  }

  private isValidIncrement(value: number): boolean {
    if (!this.data?.incrementAmount || this.data.incrementAmount === 0) {
      return true;
    }

    const incrementInCents = this.data.incrementAmount * 100;
    return value % incrementInCents === 0;
  }

  private updateAmountValue(val: string): void {
    this.amountValue = val;

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = this.amountValue;
    }

    this.cdr.detectChanges();
  }
}
